import { type FC, memo } from "react";
import { Link, LinkKind, Text } from "@stubhub/broadway-web";
import type { LeagueTeamRow } from "src/http/api-types";
import { SeoTable, type SeoTableColumn } from "src/website-apps/shared/components/seo-table/SeoTable";

interface Props {
  rows: readonly LeagueTeamRow[];
  leagueName?: string;
}

type ConferenceTeamRow = {
  index: number;
  teamsByConference: Record<string, LeagueTeamRow | undefined>;
};

export const LeagueTeamsTable: FC<Props> = memo(({ rows }) => {
  const conferences: string[] = [];
  const rowsByConference: Record<string, LeagueTeamRow[]> = {};
  for (const row of rows) {
    const conference = row.conference ?? "";
    if (!(conference in rowsByConference)) {
      rowsByConference[conference] = [];
      conferences.push(conference);
    }
    rowsByConference[conference]!.push(row);
  }

  const maxRowCount = conferences.reduce(
    (max, conference) => Math.max(max, rowsByConference[conference]?.length ?? 0),
    0,
  );

  const pairedRows: ConferenceTeamRow[] = Array.from({ length: maxRowCount }, (_, index) => ({
    index,
    teamsByConference: conferences.reduce<Record<string, LeagueTeamRow | undefined>>((acc, conference) => {
      acc[conference] = rowsByConference[conference]?.[index];
      return acc;
    }, {}),
  }));

  const columns: SeoTableColumn<ConferenceTeamRow>[] = conferences.map((conference) => ({
    header: conference,
    renderCell: (row) => {
      const team = row.teamsByConference[conference];
      if (!team) {
        return null;
      }
      return team.url ? (
        <Link href={team.url} kind={LinkKind.Ghost}>
          <Text variant="body" size="small" as="span">
            {team.name}
          </Text>
        </Link>
      ) : (
        <Text variant="body" size="small" as="span">
          {team.name}
        </Text>
      );
    },
  }));

  return <SeoTable rows={pairedRows} columns={columns} rowKey={(row) => row.index} />;
});
LeagueTeamsTable.displayName = "LeagueTeamsTable";
